import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Input,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import { getSearchItemName } from '@core/util/teranet';
import {
	ExecutionSolicitor,
	WritExecution,
	WritName,
	WritSearchItem,
} from '@models/teranet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { dispatch, select, Select } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
	DebtDialogComponent,
	DebtDialogData,
} from '../../../debts/debt-dialog';
import { DEFAULT_REQUISITION_DIMENSIONS } from '../../../documents/requisitions/modals';
import {
	INewMatterRequisitionModalData,
	NewRequisitionModalComponent,
} from '../../../documents/requisitions/modals/matter-requisition/new-requisition-modal/new-requisition-modal.component';
import { UpdateRequisitionModalComponent } from '../../../documents/requisitions/modals/matter-requisition/update-requisition-modal/update-requisition-modal.component';
import {
	SearchItemComponent,
	SearchItemComponentClose,
	SearchItemComponentData,
} from '../search/search-item/search-item.component';
import { GroupedWrit, WritSearch } from './results.component.models';
import { TeranetWritResultSelectors } from './teranet-writ-results.selector';
import { TestHooks } from './results.hooks';
import { MatterClientType } from '@models/matter';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { SaleWritActions, SaleWritState } from 'src/app/shared/state/sale_writ';

interface VM {
	writs: { [id: string]: GroupedWrit };
	currentSearchId: string;
	currentWritId: string;
	selectedExecutions: string[];
	selectedWritsCount: number;
}

const defaultVM: VM = {
	writs: {},
	currentSearchId: null,
	currentWritId: null,
	selectedExecutions: [],
	selectedWritsCount: 0,
};

@UntilDestroy()
@Component({
	selector: 'app-writ-results',
	templateUrl: './results.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class WritResultsComponent extends BaseComponent<VM> implements OnInit {
	drawerService = inject(DrawerService);
	SearchWrits = dispatch(SaleWritActions.SearchWrits);
	PurchaseExecution = dispatch(SaleWritActions.PurchaseExecution);

	@Input() hideButton = false;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(TeranetWritResultSelectors.groupByName)
	writsGroupedByName$: Observable<{ [id: string]: GroupedWrit }>;

	testHooks = TestHooks;
	MATTER_CLIENT_TYPE = MatterClientType;
	loading_ = select(SaleWritState.loading);

	constructor(
		private dialog: MatDialog,
		private snackbarService: SnackbarService,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.writsGroupedByName$.pipe(untilDestroyed(this)).subscribe((writs) => {
			if (!writs || !Object.keys(writs).length) return;
			const sortedWrits = {};
			Object.entries(writs).forEach(([key, value]) => {
				sortedWrits[key] = {
					...value,
					writSearches: Object.entries(value.writSearches)
						.sort(([, aValue], [, bValue]) => {
							const dateA = new Date(aValue.certificate?.request_date);
							const dateB = new Date(bValue.certificate?.request_date);
							if (!aValue.certificate) return -1;
							if (!bValue.certificate) return 1;
							return dateB.getTime() - dateA.getTime();
						})
						.reduce((acc, [key, value]) => {
							acc[key] = value;
							return acc;
						}, {}),
				};
			});
			const existingWrits = Object.entries(this.VM.writs).reduce(
				(acc, [key, value]) => {
					acc[key] = {
						...value,
						selected: false,
					};
					return acc;
				},
				{},
			);
			const newWrits = {
				...existingWrits,
				...sortedWrits,
			};
			const currentWritId = this.VM.currentWritId || Object.keys(writs)[0];

			const currentSearchId =
				this.VM.currentSearchId ||
				this.defaultSearchId(currentWritId, newWrits);
			this.VM = {
				writs: newWrits,
				currentWritId,
				currentSearchId,
				selectedWritsCount: Object.values(writs).filter((x) => x.selected)
					.length,
			};
		});
	}

	getSortedSearches(writ: GroupedWrit) {
		return Object.values(writ.writSearches).sort(
			(a, b) =>
				new Date(b.certificate?.request_date).getTime() -
				new Date(a.certificate?.request_date).getTime(),
		);
	}

	defaultSearchId(currentWritId, writs) {
		return Object.keys(writs[currentWritId].writSearches).sort((a, b) => {
			const dateA = new Date(writs[currentWritId].writSearches[a].date);
			const dateB = new Date(writs[currentWritId].writSearches[b].date);
			return dateB.getTime() - dateA.getTime();
		})[0];
	}

	onClickWrit(writKey: string) {
		this.VM = {
			currentWritId: writKey,
			currentSearchId: this.defaultSearchId(writKey, this.VM.writs),
			selectedExecutions: [],
		};
	}

	onClickWritSearch(writSearchKey: string) {
		this.VM = {
			currentSearchId: writSearchKey,
		};
	}

	toggleSelectWrit(key: string) {
		const newWrits = {
			...this.VM.writs,
			[key]: {
				...this.VM.writs[key],
				selected: !this.VM.writs[key].selected,
			},
		};
		this.VM = {
			writs: newWrits,
			selectedWritsCount: Object.values(newWrits).filter((x) => x.selected)
				.length,
		};
	}

	toggleSelectAllExecutions() {
		if (this.VM.selectedExecutions.length !== 0) {
			this.VM = {
				selectedExecutions: [],
			};
		} else {
			this.VM = {
				selectedExecutions: Object.values(
					this.VM.writs[this.VM.currentWritId].writSearches[
						this.VM.currentSearchId
					].executions,
				)
					.filter((x) => !x.execution?.certificate)
					.map((x) => x.execution_number),
			};
		}
	}

	toggleSelectExecution(executionNumber: string) {
		this.VM = {
			selectedExecutions: this.VM.selectedExecutions.includes(executionNumber)
				? this.VM.selectedExecutions.filter((x) => x !== executionNumber)
				: [...this.VM.selectedExecutions, executionNumber],
		};
	}

	showSelectedCertificate(certificate, name) {
		const fileName = `${name}_${certificate.type}`.replace(/\./g, '');
		const data: PdfModalData = {
			googleSrcUrl: certificate.uploaded_document_path,
			fileName,
		};
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: fileName,
			noStyle: true,
		});
	}

	searchSelectedGroupedWrits() {
		const searchItems: WritSearchItem[] = Object.values(this.VM.writs)
			.filter((x) => x.selected)
			.map((writ) => ({
				...writ.search,
				item: { ...writ.search.item, id: writ.clientId },
			}));

		this.SearchWrits(searchItems);
	}

	showSelectedExecution(certificate, executionNumber) {
		const fileName = `Execution_${executionNumber}`.replace(/\./g, '');
		const data: PdfModalData = {
			googleSrcUrl: certificate.uploaded_document_path,
			fileName,
		};

		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: fileName,
			noStyle: true,
		});
	}

	getSolicitorInformation(solicitor: ExecutionSolicitor) {
		if (!solicitor) {
			return '';
		}

		let solicitorInformation = 'Solicitor information:';
		if (solicitor?.Firmname?.length > 0) {
			solicitorInformation += `\n${solicitor.Firmname}`;
		}
		const name = [solicitor?.Givenname, solicitor?.Surname]
			.filter((x) => !!x)
			.join(' ');
		if (name?.length > 0) {
			solicitorInformation += `\n${name}`;
		}
		if (solicitor?.Address?.length > 0) {
			solicitorInformation += `\n${solicitor?.Address}`;
		}

		return solicitorInformation;
	}

	getWritPayeeName(execution: WritExecution) {
		const names = [];

		execution?.creditors?.forEach((x) => {
			if (x.Firstname?.length > 0 || x.Lastname?.length > 0) {
				const name = [x.Firstname, x.Lastname].filter((x) => !!x).join(' ');
				names.push(name);
			} else if (x.CompanyName?.length > 0) {
				names.push(x.CompanyName);
			}
		});

		return names.join(', ');
	}

	openBillAndDebt(debtId: string) {
		this.dialog.open(DebtDialogComponent, { data: { debtId } });
	}

	addBillAndDebt(writ: WritName) {
		if (writ) {
			const debtDialogData: DebtDialogData = {
				matterId: this.matterId,
				payeeName: this.getWritPayeeName(writ.execution),
				itemBeingPaid: 'Judgment',
				payeeAddress: {
					street: writ?.execution?.creditors?.[0]?.Address,
					city: writ?.execution?.creditors?.[0]?.City,
					province: writ?.execution?.creditors?.[0]?.Province,
					postcode: writ?.execution?.creditors?.[0]?.PostalCode,
					country: writ?.execution?.creditors?.[0]?.Country,
				},
				payeePhone: writ?.execution?.creditors?.[0]?.Telephone,
				payeeEmail: writ?.execution?.creditors?.[0]?.Email,
				payeeFax: writ?.execution?.creditors?.[0]?.Fax,
				executionNumber: writ?.execution_number,
				notes: this.getSolicitorInformation(writ?.execution?.solicitors?.[0]),
			};

			this.dialog.open(DebtDialogComponent, { data: debtDialogData });
		}
	}

	openRequisition(selectedRequisitionId: string) {
		this.dialog.open(UpdateRequisitionModalComponent, {
			data: { selectedRequisitionId },
		});
	}

	trackByWritSearch(index, writSearch: WritSearch) {
		return writSearch.id;
	}

	trackByExecution(index, execution: WritName) {
		return execution.execution_number;
	}

	addSearchItem(searchItem: WritSearchItem) {
		const name = getSearchItemName(searchItem);
		const id = name?.toLowerCase();

		if (!this.VM.writs[id]) {
			this.VM = {
				currentWritId: id,
				selectedWritsCount: this.VM.selectedWritsCount + 1,
				writs: {
					...this.VM.writs,
					[id]: {
						id: name?.toLowerCase(),
						name,
						lowercaseName: name?.toLowerCase(),
						searchesCount: 0,
						writSearches: {},
						search: searchItem,
						selected: true,
						searching: false,
					},
				},
			};
		}
	}

	openSearchDialog(data: SearchItemComponentData) {
		return this.dialog
			.open<
				SearchItemComponent,
				SearchItemComponentData,
				SearchItemComponentClose
			>(SearchItemComponent, {
				data,
				maxHeight: '500px',
				width: '100%',
				maxWidth: '500px',
			})
			.afterClosed()
			.pipe(
				take(1),
				filter((value) => !!value),
			)
			.subscribe((value) => {
				this.addSearchItem(value);
			});
	}

	addRequisition(writ: WritName) {
		if (writ) {
			const fields = {
				executionsNumber: writ.execution_number,
			};

			const data: INewMatterRequisitionModalData = {
				fields,
				searchTerm: 'Execution',
			};

			this.dialog
				.open(NewRequisitionModalComponent, {
					...DEFAULT_REQUISITION_DIMENSIONS,
					data,
				})
				.afterClosed()
				.pipe(take(1))
				.subscribe((res) => {
					if (res) {
						this.snackbarService.openSnackBar({
							text: 'Requisition Added',
							type: 'success',
							icon: ICON.CHECK,
						});
					}
				});
		}
	}

	purchaseSelectedExecutions() {
		const selectedWritSearch =
			this.VM.writs[this.VM.currentWritId]?.writSearches[
				this.VM.currentSearchId
			].executions;
		const executionsToPurchase = this.VM.selectedExecutions.map(
			(x) => selectedWritSearch[x],
		);

		executionsToPurchase?.forEach((x) => {
			if (!x.execution?.certificate) {
				this.PurchaseExecution({
					writId: this.VM.currentSearchId,
					executionNumber: x.execution_number,
				});
			}
		});

		this.VM = {
			selectedExecutions: [],
		};
	}
}
